// Curated emoji set for naming warehouse zones (區). Each entry carries Chinese
// keywords (plus English ones) so the picker can be searched in 中文 or English.
// Kept dependency-free and small on purpose — the icon is optional and stored as the leading part of the name
// (e.g. "📦 A" renders as "📦 A 區").

export interface EmojiOption {
  emoji: string;
  keywords: string[];
}

export const WAREHOUSE_EMOJIS: EmojiOption[] = [
  // 字母 / 數字標記
  { emoji: "🅰️", keywords: ["A", "字母", "標記", "letter", "mark"] },
  { emoji: "🅱️", keywords: ["B", "字母", "標記", "letter", "mark"] },
  { emoji: "🆎", keywords: ["AB", "字母", "標記", "letter", "mark"] },
  { emoji: "🅾️", keywords: ["O", "字母", "標記", "letter", "mark"] },
  { emoji: "🔠", keywords: ["字母", "英文", "大寫", "標記", "letters", "uppercase", "abc", "mark"] },
  { emoji: "🔢", keywords: ["數字", "號碼", "編號", "numbers", "number", "123"] },
  { emoji: "#️⃣", keywords: ["井號", "編號", "號碼", "hash", "number"] },
  // 倉儲 / 物流
  { emoji: "📦", keywords: ["箱子", "包裹", "貨物", "紙箱", "庫存", "box", "package", "cargo", "stock", "inventory"] },
  { emoji: "🏭", keywords: ["工廠", "廠房", "倉庫", "製造", "factory", "plant", "warehouse"] },
  { emoji: "🏢", keywords: ["大樓", "辦公", "建築", "building", "office"] },
  { emoji: "🏬", keywords: ["賣場", "百貨", "商場", "store", "mall", "department"] },
  { emoji: "🏗️", keywords: ["工地", "建造", "施工", "construction", "site"] },
  { emoji: "🚚", keywords: ["卡車", "貨車", "運輸", "配送", "truck", "delivery", "shipping"] },
  { emoji: "🚛", keywords: ["聯結車", "大貨車", "運輸", "truck", "lorry", "shipping"] },
  { emoji: "🛒", keywords: ["購物車", "推車", "採購", "cart", "shopping", "purchase"] },
  { emoji: "🛗", keywords: ["電梯", "升降", "elevator", "lift"] },
  { emoji: "🗄️", keywords: ["檔案櫃", "櫃子", "收納", "抽屜", "cabinet", "file", "storage", "drawer"] },
  { emoji: "📥", keywords: ["收件", "入庫", "進貨", "接收", "inbox", "inbound", "receiving"] },
  { emoji: "📤", keywords: ["寄件", "出庫", "出貨", "發送", "outbox", "outbound", "shipping"] },
  { emoji: "🏷️", keywords: ["標籤", "吊牌", "價格", "標記", "tag", "label", "price"] },
  { emoji: "📋", keywords: ["清單", "表單", "文件", "盤點", "clipboard", "list", "checklist", "stocktake"] },
  // 溫控
  { emoji: "❄️", keywords: ["冷凍", "冷藏", "低溫", "雪", "冰", "frozen", "cold", "freezer", "snow"] },
  { emoji: "🧊", keywords: ["冰塊", "冷凍", "結冰", "ice", "frozen"] },
  { emoji: "🔥", keywords: ["火", "高溫", "易燃", "熱", "fire", "hot", "flammable"] },
  { emoji: "☀️", keywords: ["太陽", "常溫", "乾燥", "晴", "sun", "ambient", "dry"] },
  { emoji: "💧", keywords: ["水", "液體", "潮濕", "防水", "water", "liquid", "wet", "waterproof"] },
  { emoji: "🌡️", keywords: ["溫度", "溫控", "溫度計", "temperature", "thermometer"] },
  // 危險 / 警示
  { emoji: "⚠️", keywords: ["警告", "注意", "危險", "warning", "caution", "danger"] },
  { emoji: "☢️", keywords: ["輻射", "核能", "危險", "radiation", "nuclear", "danger"] },
  { emoji: "☣️", keywords: ["生物危害", "病毒", "危險", "biohazard", "danger"] },
  { emoji: "⚗️", keywords: ["化學", "實驗", "藥劑", "chemical", "lab"] },
  { emoji: "🧪", keywords: ["試管", "化學", "實驗", "藥品", "test tube", "chemical", "lab"] },
  { emoji: "🧯", keywords: ["滅火器", "消防", "安全", "fire extinguisher", "safety"] },
  { emoji: "⚡", keywords: ["電力", "高壓", "電", "electric", "high voltage", "power"] },
  { emoji: "🔒", keywords: ["鎖", "上鎖", "保管", "安全", "lock", "locked", "secure"] },
  // 材料 / 工具
  { emoji: "🔩", keywords: ["螺絲", "螺栓", "五金", "零件", "bolt", "nut", "hardware", "parts"] },
  { emoji: "🔧", keywords: ["扳手", "工具", "維修", "wrench", "tool", "repair"] },
  { emoji: "🔨", keywords: ["鎚子", "榔頭", "工具", "hammer", "tool"] },
  { emoji: "🛠️", keywords: ["工具", "維修", "五金", "tools", "repair", "hardware"] },
  { emoji: "🪛", keywords: ["螺絲起子", "工具", "screwdriver", "tool"] },
  { emoji: "🧱", keywords: ["磚", "磚塊", "建材", "brick", "building material"] },
  { emoji: "🪵", keywords: ["木材", "木頭", "木料", "wood", "timber", "lumber"] },
  { emoji: "🪨", keywords: ["石頭", "石材", "礦石", "rock", "stone", "ore"] },
  { emoji: "⛓️", keywords: ["鏈條", "鐵鏈", "五金", "chain", "hardware"] },
  // 電子
  { emoji: "💻", keywords: ["電腦", "筆電", "電子", "computer", "laptop", "electronics"] },
  { emoji: "🔌", keywords: ["插頭", "電源", "電器", "plug", "power", "electrical"] },
  { emoji: "🔋", keywords: ["電池", "電力", "充電", "battery", "power", "charge"] },
  { emoji: "📱", keywords: ["手機", "電子", "行動", "phone", "mobile", "electronics"] },
  { emoji: "💡", keywords: ["燈泡", "照明", "燈", "light bulb", "lighting", "lamp"] },
  // 食品 / 生鮮
  { emoji: "🍎", keywords: ["蘋果", "水果", "食品", "生鮮", "apple", "fruit", "food", "fresh"] },
  { emoji: "🥦", keywords: ["蔬菜", "青菜", "生鮮", "vegetable", "broccoli", "fresh"] },
  { emoji: "🍞", keywords: ["麵包", "烘焙", "食品", "bread", "bakery", "food"] },
  { emoji: "🥫", keywords: ["罐頭", "食品", "加工", "can", "canned", "food"] },
  { emoji: "🍺", keywords: ["飲料", "酒", "啤酒", "beverage", "beer", "drink"] },
  { emoji: "☕", keywords: ["咖啡", "飲料", "熱飲", "coffee", "drink", "hot"] },
  // 顏色標記
  { emoji: "🔴", keywords: ["紅色", "紅", "標記", "red", "mark"] },
  { emoji: "🟠", keywords: ["橘色", "橙", "標記", "orange", "mark"] },
  { emoji: "🟡", keywords: ["黃色", "黃", "標記", "yellow", "mark"] },
  { emoji: "🟢", keywords: ["綠色", "綠", "標記", "green", "mark"] },
  { emoji: "🔵", keywords: ["藍色", "藍", "標記", "blue", "mark"] },
  { emoji: "🟣", keywords: ["紫色", "紫", "標記", "purple", "mark"] },
  { emoji: "🟤", keywords: ["棕色", "咖啡色", "標記", "brown", "mark"] },
  { emoji: "⚫", keywords: ["黑色", "黑", "標記", "black", "mark"] },
  { emoji: "⚪", keywords: ["白色", "白", "標記", "white", "mark"] },
  // 常用符號
  { emoji: "⭐", keywords: ["星星", "重要", "收藏", "星", "star", "important", "favorite"] },
  { emoji: "❤️", keywords: ["愛心", "紅心", "喜歡", "heart", "love", "like"] },
  { emoji: "✅", keywords: ["勾", "完成", "確認", "打勾", "check", "done", "confirm"] },
  { emoji: "❗", keywords: ["驚嘆號", "重要", "注意", "exclamation", "important", "attention"] },
  { emoji: "📌", keywords: ["圖釘", "釘選", "標記", "重點", "pin", "pinned", "mark"] },
  { emoji: "🚩", keywords: ["旗子", "旗標", "標記", "flag", "mark"] },
  { emoji: "🎯", keywords: ["靶", "目標", "重點", "target", "goal", "focus"] },
  { emoji: "🗂️", keywords: ["分類", "檔案", "分隔", "category", "files", "dividers"] },
];

// Emojis sorted longest-first so multi-codepoint sequences (with variation
// selectors) are matched before shorter ones when detecting a leading icon.
const EMOJIS_BY_LENGTH = [...WAREHOUSE_EMOJIS]
  .map((e) => e.emoji)
  .sort((a, b) => b.length - a.length);

/** Split a stored name into an optional leading emoji and the remaining text. */
export function splitIconName(value: string): { icon: string; text: string } {
  for (const emoji of EMOJIS_BY_LENGTH) {
    if (value.startsWith(emoji)) {
      return { icon: emoji, text: value.slice(emoji.length).trimStart() };
    }
  }
  return { icon: "", text: value };
}

/** Combine an optional emoji with a name (emoji goes at the 開頭). */
export function joinIconName(icon: string, text: string): string {
  const trimmed = text.trim();
  return icon ? `${icon} ${trimmed}` : trimmed;
}

/** Filter emojis by a Chinese (or latin) keyword query. */
export function searchEmojis(query: string): EmojiOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return WAREHOUSE_EMOJIS;
  return WAREHOUSE_EMOJIS.filter(
    (e) =>
      e.emoji === query ||
      e.keywords.some((k) => k.toLowerCase().includes(q))
  );
}
